import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import {
  priceFieldProps,
  priceRegisterConfig,
  useFieldValidation,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import StartEndAddressFields from './Fields/StartEndAddressFields'
import StartEndDateTimeFields from './Fields/StartEndDateTimeFields'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import type { CreateFerryItemViewModel } from '../Create/models'
import type { PatchFerryItemViewModel } from '../Edit/models'

import type { AutocompletePlaceParam } from 'tripPlanner/types/google'

const AUTOCOMPLETE_TYPES: AutocompletePlaceParam = []

interface Props {
  tripId: string
}

function FerryFields({ tripId }: Props) {
  const { urlRegisterConfig } = useFieldValidation()
  const intl = useIntl()
  const methods = useFormContext<
    CreateFerryItemViewModel | PatchFerryItemViewModel
  >()
  const { control, watch, register } = methods
  const watchBooked = watch('isBooked')

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  const departureDateLabel = intl.formatMessage({
    id: '6rzFvwc8OrbwJla6-if9',
    defaultMessage: 'Departure date',
    description: 'Form label for departure date field in ferry trip item editor',
  })

  const arrivalDateLabel = intl.formatMessage({
    id: 'rd2AQeDJB-CDl1jGkALf',
    defaultMessage: 'Arrival date',
    description: 'Form label for arrival date field in ferry trip item editor',
  })

  const operatorLabel = intl.formatMessage({
    id: 'wTwPbx3csT7HyUX4K2Bk',
    defaultMessage: 'Operator',
    description: 'Form label for operator name field in ferry trip item editor',
  })

  const websiteLabel = intl.formatMessage({
    id: 'l1neeRXpdaO-nIqTdPP8',
    defaultMessage: 'Website',
    description: 'Form label for website URL field in ferry trip item editor',
  })

  const confirmationNumberLabel = intl.formatMessage({
    id: 'yikgiZGne4c85oRNKXWU',
    defaultMessage: 'Confirmation number',
    description: 'Form label for booking confirmation number in ferry trip item editor',
  })

  const seatLabel = intl.formatMessage({
    id: 'JpLxUETPvVcboOYggT7d',
    defaultMessage: 'Seat',
    description: 'Form label for seat field in ferry trip item editor',
  })

  const seatPlaceholder = intl.formatMessage({
    id: 'JP10qObRlxne2CTtBjlt',
    defaultMessage: 'Seat number',
    description: 'Placeholder text for seat number input in ferry trip item editor',
  })

  const priceLabel = intl.formatMessage({
    id: 'fPFCqaNapzElTdKSibLA',
    defaultMessage: 'Price',
    description: 'Form label for price field in ferry trip item editor',
  })

  const additionalNotesLabel = intl.formatMessage({
    id: 'Ha00KFnX0vcfHvx4Ybvl',
    defaultMessage: 'Additional notes',
    description: 'Form label for additional notes field in ferry trip item editor',
  })

  const notesPlaceholder = intl.formatMessage({
    id: 'ouFOEBx1y9WSBEhgvgGC',
    defaultMessage: 'Add notes about this booking',
    description: 'Placeholder text for notes textarea in ferry trip item editor',
  })

  return (
    <>
      <StartEndAddressFields
        autocompleteTypes={AUTOCOMPLETE_TYPES}
        tripId={tripId}
      />
      <StartEndDateTimeFields
        startDateLabel={departureDateLabel}
        endDateLabel={arrivalDateLabel}
        defaultMonth={defaultMonth}
        defaultYear={defaultYear}
      />
      <Input
        {...register('operatorName')}
        label={operatorLabel}
        placeholder={operatorLabel}
      />

      <Input
        {...register('url', { ...urlRegisterConfig })}
        label={websiteLabel}
        placeholder={websiteLabel}
      />

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />

      {watchBooked && (
        <>
          <Input
            {...register('confirmationCode')}
            label={confirmationNumberLabel}
            placeholder={confirmationNumberLabel}
          />

          <Input {...register('seat')} label={seatLabel} placeholder={seatPlaceholder} />
        </>
      )}

      <Input
        {...register('price', { ...priceRegisterConfig })}
        label={priceLabel}
        placeholder={priceLabel}
        {...priceFieldProps}
      />

      <TextArea
        {...register('notes')}
        placeholder={notesPlaceholder}
        label={additionalNotesLabel}
        resize="vertical"
      />
    </>
  )
}

export default FerryFields
